import React, {useEffect, useState} from 'react';
import styled from "styled-components";
import {groupFreighter, groupUserAdmin, groupNotApproved} from "../../../../../../config/acl/roles/groups";
import {useAppDispatch, useAppSelector} from "../../../../../../store/store";
import {
  CustomLoadedFileWrapper,
  CustomLoadedFileWrapperTitle,
  CustomUpload
} from "../../../../../../components/UI/AntCustom/CustomUpload";
import PdfIcon from "../../../../../../media/icons/pdf.svg?react";
import {onPdfFilePreview} from "../../../../../../utils/helpers/fileHelpers/onPdfFilePreview";
import Button from "antd/es/button/button";
import {RcFile, UploadFile} from "antd/es/upload";
import {CustomButton} from "../../../../../../components/UI/AntCustom/CustomButton";
import {CustomSwitch} from "../../../../../../components/UI/AntCustom/CustomSwitch";
import {FileAcceptTypes} from "../../../../../../utils/constans/files/fileAcceptTypes";
import {
  CompanyWarningStatusEnum,
  isCompanyOnLoadDocuments,
  isCompanyOnModeration,
  isCompanyRejected,
  useCompanyWarningStatus
} from "../../hooks/useCompanyWarningStatus";

import {
  clearInternationalFlightToggle,
  clearInternationalFlightUpload,
  clearLoadCertificates,
  internationalFlightToggle,
  internationalFlightUpload,
  loadCertificates,
} from "../../../../../../store/reducers/companyReducer/companyReducer";
import {Role} from "../../../../../../config/acl/roles/Role";
import {renderSuccessModal} from "../../../../../../components/UI/StatusModals/renderSuccessModal";
import {message} from "antd";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../../../intl/commonMessages/CommonButtonMessages";
import {mediaScreenSizes} from "../../../../../../utils/constans/styles/screenSizes";
import {validateFileSize, getFileSizeErrorMessage} from "../../../../../../utils/helpers/fileHelpers/validateFileSize";

const LicensesWrapper = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
`;

const LicensesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  column-gap: 25px;
  row-gap: 8px;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
  }
`;

const WarningInfo = styled.div<{ color: string, status: CompanyWarningStatusEnum }>`
  font-size: 16px;
  line-height: 22px;
  letter-spacing: -0.01em;
  color: ${props => props.color};
  margin: ${props => (isCompanyOnLoadDocuments(props.status) && "0 0 8px 0")
          || (isCompanyOnModeration(props.status) && "16px 0 0 0")};

  font-weight: ${props => (isCompanyOnLoadDocuments(props.status) && "500")
          || (isCompanyOnModeration(props.status) && "400")};
`;

const LoadDocumentsButtonWrapper = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  
  button {
    width: 100%;
  }
`;

const InternationalFlightsToggleWrapper = styled.div`
  margin-top: 24px;
`;

const InternationalDocumentsWrapper = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RequiredLabel = styled.span`
  margin-left: 2px;
`;

const ProfileCompanyLicensesMessages = defineMessages({
  successSend: {
    id: "profileCompanyLicenses.successSend",
    defaultMessage: "Documents have been successfully sent for moderation. Please, wait until your account is approved by the platform administrator.",
  },
  warningLoadDocs: {
    id: "profileCompanyLicenses.warningLoadDocs",
    defaultMessage: "Upload documents to pass moderation",
  },
  operatorCertificate: {
    id: "profileCompanyLicenses.operatorCertificate",
    defaultMessage: "AOC",
  },
  registrationCertificate: {
    id: "profileCompanyLicenses.registrationCertificate",
    defaultMessage: "Certificate of incorporation",
  },
  internationalFlights: {
    id: "profileCompanyLicenses.internationalFlights",
    defaultMessage: "International Flights",
  },
  caaOperatorApproval: {
    id: "profileCompanyLicenses.caaOperatorApproval",
    defaultMessage: "CAA operator approval to operate international flights",
  },
  easaTcoAuthorisation: {
    id: "profileCompanyLicenses.easaTcoAuthorisation",
    defaultMessage: "EU: EASA TCO authorisation",
  },
  faaAuthorization: {
    id: "profileCompanyLicenses.faaAuthorization",
    defaultMessage: "US: FAA authorization",
  },
  warningWaitApprove: {
    id: "profileCompanyLicenses.warningWaitApprove",
    defaultMessage: "Thank you. We will check your documents in 2 days.",
  },
  warningRejected: {
    id: "profileCompanyLicenses.warningRejected",
    defaultMessage: "Your request has been declined. To resend it for moderation you must upload updated files before submitting.",
  },
  loadDocs: {
    id: "profileCompanyLicenses.loadDocs",
    defaultMessage: "Send documents for moderation",
  },
  internationalFlightsToggleSuccess: {
    id: "profileCompanyLicenses.internationalFlightsToggleSuccess",
    defaultMessage: "International flights setting updated",
  },
  internationalFlightsUploadSuccess: {
    id: "profileCompanyLicenses.internationalFlightsUploadSuccess",
    defaultMessage: "International flight documents uploaded successfully",
  },
  uploadInternationalDocs: {
    id: "profileCompanyLicenses.uploadInternationalDocs",
    defaultMessage: "Upload documents",
  },
  caaOperatorApprovalRequired: {
    id: "profileCompanyLicenses.caaOperatorApprovalRequired",
    defaultMessage: "CAA operator approval document is required when International Flights is enabled",
  },
})

type ProfileFileType = "operator" | "registration" | "caaOperatorApproval" | "easaTcoAuthorisation" | "faaAuthorization";

export const ProfileCompanyLicenses = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {role} = useAppSelector(state => state.user.oneItemObject.item);
  const company = useAppSelector(state => state.company.oneItemObject.item);
  const isAdmin = [...groupUserAdmin, ...groupNotApproved].includes(role);
  const [operatorCertificateFile, setOperatorCertificateFile] = useState<UploadFile | undefined>(company.operatorCard);
  const [registrationCertificateFile, setRegistrationCertificateFile] = useState<UploadFile | undefined>(company.registrationCertificate);
  // International flights documents
  const [internationalFlightsEnabled, setInternationalFlightsEnabled] = useState<boolean>(company.internationalFlightsEnabled ?? false);
  const [caaOperatorApprovalFile, setCaaOperatorApprovalFile] = useState<UploadFile | undefined>(company.caaOperatorApproval);
  const [easaTcoAuthorisationFile, setEasaTcoAuthorisationFile] = useState<UploadFile | undefined>(company.easaTcoAuthorisation);
  const [faaAuthorizationFile, setFaaAuthorizationFile] = useState<UploadFile | undefined>(company.faaAuthorization);
  const {companyWarningStatus, companyWarningStatusColor} = useCompanyWarningStatus();
  const {isLoading, isSuccess, errorMessage} = useAppSelector(state => state.company.loadCertificates);
  const {isLoading: isToggleLoading, isSuccess: isToggleSuccess, errorMessage: toggleErrorMessage} = useAppSelector(state => state.company.internationalFlightToggle);
  const {isLoading: isUploadLoading, isSuccess: isUploadSuccess, errorMessage: uploadErrorMessage} = useAppSelector(state => state.company.internationalFlightUpload);

  useEffect(() => {
    // Only update files if they are loaded from server (have url) or if local file doesn't exist
    // This prevents overwriting locally selected files that haven't been uploaded yet
    // Update if: local file doesn't exist, local file is already uploaded (has url), or company file is uploaded (has url)
    // For upper documents (operator and registration), preserve locally selected files even if company file becomes undefined
    setOperatorCertificateFile(prev => {
      // If local file exists and doesn't have url (locally selected, not uploaded), ALWAYS preserve it
      // This prevents losing locally selected files when company object updates (e.g., when toggle changes)
      if (prev && !prev.url) {
        // Never overwrite locally selected file, even if company file changes
        return prev;
      }
      // Otherwise, update from company
      return company.operatorCard;
    });
    setRegistrationCertificateFile(prev => {
      // If local file exists and doesn't have url (locally selected, not uploaded), ALWAYS preserve it
      // This prevents losing locally selected files when company object updates (e.g., when toggle changes)
      if (prev && !prev.url) {
        // Never overwrite locally selected file, even if company file changes
        return prev;
      }
      // Otherwise, update from company
      return company.registrationCertificate;
    });
    setInternationalFlightsEnabled(company.internationalFlightsEnabled ?? false);
    // For international flight documents, preserve locally selected files (without url)
    // This prevents losing locally selected files when company object updates (e.g., when toggle changes)
    setCaaOperatorApprovalFile(prev => {
      // If local file exists and doesn't have url (locally selected, not uploaded), ALWAYS preserve it
      if (prev && !prev.url) {
        return prev;
      }
      // Otherwise, update from company
      return company.caaOperatorApproval;
    });
    setEasaTcoAuthorisationFile(prev => {
      // If local file exists and doesn't have url (locally selected, not uploaded), ALWAYS preserve it
      if (prev && !prev.url) {
        return prev;
      }
      // Otherwise, update from company
      return company.easaTcoAuthorisation;
    });
    setFaaAuthorizationFile(prev => {
      // If local file exists and doesn't have url (locally selected, not uploaded), ALWAYS preserve it
      if (prev && !prev.url) {
        return prev;
      }
      // Otherwise, update from company
      return company.faaAuthorization;
    });

    return () => {
      dispatch(clearLoadCertificates());
    }
  }, [company.registrationCertificate, company.operatorCard, company.internationalFlightsEnabled, company.caaOperatorApproval, company.easaTcoAuthorisation, company.faaAuthorization, dispatch])

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess) {
        renderSuccessModal(
          intl.formatMessage(ProfileCompanyLicensesMessages.successSend),
          () => window.location.reload(),
        );
      }
      if (errorMessage)
        message.error(errorMessage);
    }
  }, [dispatch, intl, isSuccess, errorMessage, isLoading])

  useEffect(() => {
    if (!isToggleLoading && isToggleSuccess) {
      message.success(intl.formatMessage(ProfileCompanyLicensesMessages.internationalFlightsToggleSuccess));
      dispatch(clearInternationalFlightToggle());
    }
    if (toggleErrorMessage) {
      message.error(toggleErrorMessage);
      dispatch(clearInternationalFlightToggle());
    }
  }, [dispatch, intl, isToggleLoading, isToggleSuccess, toggleErrorMessage])

  useEffect(() => {
    if (!isUploadLoading && isUploadSuccess) {
      message.success(intl.formatMessage(ProfileCompanyLicensesMessages.internationalFlightsUploadSuccess));
      dispatch(clearInternationalFlightUpload());
      // Reload page to update document states
      window.location.reload();
    }
    if (uploadErrorMessage) {
      message.error(uploadErrorMessage);
      dispatch(clearInternationalFlightUpload());
    }
  }, [dispatch, intl, isUploadLoading, isUploadSuccess, uploadErrorMessage])


  const canUploadDocuments = isCompanyOnLoadDocuments(companyWarningStatus) || isCompanyRejected(companyWarningStatus);
  const isRejected = isCompanyRejected(companyWarningStatus);

  // Check if there are new main documents (without URL - locally selected, not yet uploaded)
  // This is important when company is rejected - we need new documents to resubmit
  const hasNewMainDocuments = isRejected ? (
    // For rejected companies, require all required documents to be new (without url)
    // This prevents resubmitting the same documents that were already rejected
    (role === Role.freighterAdminNotApproved && (
      operatorCertificateFile && !operatorCertificateFile.url &&
      registrationCertificateFile && !registrationCertificateFile.url
    )) ||
    (role === Role.chartererAdminNotApproved && (
      registrationCertificateFile && !registrationCertificateFile.url
    ))
  ) : (
    // For companies on initial load, just check if documents exist
    (role === Role.freighterAdminNotApproved && operatorCertificateFile && registrationCertificateFile) ||
    (role === Role.chartererAdminNotApproved && registrationCertificateFile)
  );

  // For rejected companies with international flights enabled, CAA must be new (without url)
  // For initial load, CAA just needs to exist if international flights is enabled
  const hasValidCaaOperatorApproval = !internationalFlightsEnabled || (
    caaOperatorApprovalFile && (!isRejected || !caaOperatorApprovalFile.url)
  );

  const showLoadDocumentsButton = canUploadDocuments &&
    hasNewMainDocuments &&
    hasValidCaaOperatorApproval;

  // Check if there are new or changed international flight documents that need to be uploaded
  // A file is considered "new" if it doesn't have a URL (locally selected, not yet uploaded)
  const hasNewInternationalFlightDocuments = internationalFlightsEnabled && (
    (caaOperatorApprovalFile && !caaOperatorApprovalFile.url) ||
    (easaTcoAuthorisationFile && !easaTcoAuthorisationFile.url) ||
    (faaAuthorizationFile && !faaAuthorizationFile.url)
  );

  // Show unified upload button for international flights only after documents have been sent for moderation
  // (not on the initial upload stage) and if there are new documents to upload
  const showInternationalFlightUploadButton = !isCompanyOnLoadDocuments(companyWarningStatus) &&
    hasNewInternationalFlightDocuments;

  // Unified button logic: show button if either main documents need to be sent OR international flight documents need to be updated
  const showUnifiedUploadButton = showLoadDocumentsButton || showInternationalFlightUploadButton;

  const onFileLoad = (file: RcFile, type: ProfileFileType) => {
    if (!validateFileSize(file)) {
      message.error(getFileSizeErrorMessage(intl));
      return false;
    }
    if (type === "operator")
      setOperatorCertificateFile(file);
    else if (type === "registration")
      setRegistrationCertificateFile(file);
    else if (type === "caaOperatorApproval")
      setCaaOperatorApprovalFile(file);
    else if (type === "easaTcoAuthorisation")
      setEasaTcoAuthorisationFile(file);
    else if (type === "faaAuthorization")
      setFaaAuthorizationFile(file);
    return false;
  }

  const onFileRemove = (type: ProfileFileType) => {
    if (type === "operator")
      setOperatorCertificateFile(undefined);
    else if (type === "registration")
      setRegistrationCertificateFile(undefined);
    else if (type === "caaOperatorApproval")
      setCaaOperatorApprovalFile(undefined);
    else if (type === "easaTcoAuthorisation")
      setEasaTcoAuthorisationFile(undefined);
    else if (type === "faaAuthorization")
      setFaaAuthorizationFile(undefined);
  }

  const onCertificatesUpload = () => {
    if (registrationCertificateFile) {
      // Validate: if International Flights toggle is enabled, CAA operator approval is required
      if (internationalFlightsEnabled && !caaOperatorApprovalFile) {
        message.error(intl.formatMessage(ProfileCompanyLicensesMessages.caaOperatorApprovalRequired));
        return;
      }

      dispatch(loadCertificates({
        company: company,
        registrationCertificate: registrationCertificateFile,
        operatorCertificate: operatorCertificateFile,
        // Include international flight documents if toggle is enabled and files are selected
        internationalFlightsEnabled: internationalFlightsEnabled,
        caaOperatorApproval: internationalFlightsEnabled ? caaOperatorApprovalFile : undefined,
        easaTcoAuthorisation: internationalFlightsEnabled ? easaTcoAuthorisationFile : undefined,
        faaAuthorization: internationalFlightsEnabled ? faaAuthorizationFile : undefined,
      }))
    }
  }

  const onInternationalFlightToggleChange = (checked: boolean) => {
    setInternationalFlightsEnabled(checked);
    dispatch(internationalFlightToggle({enabled: checked}));
  }

  const onInternationalFlightFilesUpload = () => {
    dispatch(internationalFlightUpload({
      caaOperatorApproval: caaOperatorApprovalFile,
      easaTcoAuthorisation: easaTcoAuthorisationFile,
      faaAuthorization: faaAuthorizationFile,
    }));
  }

  // Unified handler for document upload button
  const onUnifiedUpload = () => {
    if (showLoadDocumentsButton) {
      // Send main documents (and international flight documents if enabled)
      onCertificatesUpload();
    } else if (showInternationalFlightUploadButton) {
      // Send only international flight documents
      onInternationalFlightFilesUpload();
    }
  }

  // Determine button text and loading state based on which action is being performed
  const getButtonText = () => {
    if (showLoadDocumentsButton) {
      return intl.formatMessage(ProfileCompanyLicensesMessages.loadDocs);
    }
    if (showInternationalFlightUploadButton) {
      return intl.formatMessage(ProfileCompanyLicensesMessages.uploadInternationalDocs);
    }
    return intl.formatMessage(ProfileCompanyLicensesMessages.loadDocs);
  }

  const getButtonLoading = () => {
    if (showLoadDocumentsButton) {
      return isLoading;
    }
    if (showInternationalFlightUploadButton) {
      return isUploadLoading;
    }
    return false;
  }

  return (
    <LicensesWrapper>
      {isCompanyOnLoadDocuments(companyWarningStatus) &&
        <WarningInfo color={companyWarningStatusColor} status={companyWarningStatus}>
          {intl.formatMessage(ProfileCompanyLicensesMessages.warningLoadDocs)}
        </WarningInfo>}

      <LicensesList>
        {groupFreighter.includes(role) &&
          <CustomLoadedFileWrapper style={{minWidth: 235}}>
            <CustomLoadedFileWrapperTitle>{intl.formatMessage(ProfileCompanyLicensesMessages.operatorCertificate)}</CustomLoadedFileWrapperTitle>
            <CustomUpload
              accept={FileAcceptTypes.DOCUMENT}
              beforeUpload={(file) => onFileLoad(file, "operator")}
              fileList={operatorCertificateFile ? [operatorCertificateFile] : []}
              maxCount={1}
              onPreview={onPdfFilePreview}
              onRemove={isAdmin ? () => onFileRemove("operator") : undefined}
            >
              {canUploadDocuments && isAdmin &&
                <Button icon={<PdfIcon/>} type={"dashed"}>
                  <div>{operatorCertificateFile ? intl.formatMessage(CommonButtonMessages.replace) : intl.formatMessage(CommonButtonMessages.upload)}</div>
                </Button>}
            </CustomUpload>
          </CustomLoadedFileWrapper>
        }

        <CustomLoadedFileWrapper style={{minWidth: 235}}>
          <CustomLoadedFileWrapperTitle>{intl.formatMessage(ProfileCompanyLicensesMessages.registrationCertificate)}</CustomLoadedFileWrapperTitle>
          <CustomUpload
            accept={FileAcceptTypes.DOCUMENT}
            beforeUpload={(file) => onFileLoad(file, "registration")}
            fileList={registrationCertificateFile ? [registrationCertificateFile] : []}
            maxCount={1}
            onPreview={onPdfFilePreview}
            onRemove={isAdmin ? () => onFileRemove("registration") : undefined}
          >
            {canUploadDocuments && isAdmin &&
              <CustomButton icon={<PdfIcon/>} type={"dashed"}>
                <div>{registrationCertificateFile ? intl.formatMessage(CommonButtonMessages.replace) : intl.formatMessage(CommonButtonMessages.upload)}</div>
              </CustomButton>}
          </CustomUpload>
        </CustomLoadedFileWrapper>
      </LicensesList>

      {/* International Flights Toggle and Documents */}
      {groupFreighter.includes(role) && (
        <InternationalFlightsToggleWrapper>
          {isAdmin ? (
            <CustomSwitch
                labelFirst
              checked={internationalFlightsEnabled}
              onChange={onInternationalFlightToggleChange}
              label={intl.formatMessage(ProfileCompanyLicensesMessages.internationalFlights)}
            />
          ) : (
            <div style={{fontWeight: 500, fontSize: 14, lineHeight: '22px', color: '#000000', marginBottom: internationalFlightsEnabled ? 16 : 0}}>
              {intl.formatMessage(ProfileCompanyLicensesMessages.internationalFlights)}: {internationalFlightsEnabled ? intl.formatMessage({id: 'displayHelpers.displayYesOrNo.yes', defaultMessage: 'Yes'}) : intl.formatMessage({id: 'displayHelpers.displayYesOrNo.no', defaultMessage: 'No'})}
            </div>
          )}
          
          {internationalFlightsEnabled && (
            <InternationalDocumentsWrapper>
              {/* CAA Operator Approval - Required */}
              <CustomLoadedFileWrapper style={{minWidth: 235, maxWidth: 495}}>
                <CustomLoadedFileWrapperTitle>
                  {intl.formatMessage(ProfileCompanyLicensesMessages.caaOperatorApproval)}
                  <RequiredLabel>*</RequiredLabel>
                </CustomLoadedFileWrapperTitle>
                <CustomUpload
                  accept={FileAcceptTypes.DOCUMENT}
                  beforeUpload={(file) => onFileLoad(file, "caaOperatorApproval")}
                  fileList={caaOperatorApprovalFile ? [caaOperatorApprovalFile] : []}
                  maxCount={1}
                  onPreview={onPdfFilePreview}
                  onRemove={isAdmin ? () => onFileRemove("caaOperatorApproval") : undefined}>
                    {isAdmin && <Button icon={<PdfIcon/>} type={"dashed"}>
                      <div>{caaOperatorApprovalFile ? intl.formatMessage(CommonButtonMessages.replace) : intl.formatMessage(CommonButtonMessages.upload)}</div>
                    </Button>}
                </CustomUpload>
              </CustomLoadedFileWrapper>

              <LicensesList>
              {/* EASA TCO Authorisation - Optional */}
                <CustomLoadedFileWrapper style={{minWidth: 235}}>
                  <CustomLoadedFileWrapperTitle>
                    {intl.formatMessage(ProfileCompanyLicensesMessages.easaTcoAuthorisation)}
                  </CustomLoadedFileWrapperTitle>
                  <CustomUpload
                      accept={FileAcceptTypes.DOCUMENT}
                      beforeUpload={(file) => onFileLoad(file, "easaTcoAuthorisation")}
                      fileList={easaTcoAuthorisationFile ? [easaTcoAuthorisationFile] : []}
                      maxCount={1}
                      onPreview={onPdfFilePreview}
                      onRemove={isAdmin ? () => onFileRemove("easaTcoAuthorisation") : undefined}
                  >
                    {isAdmin && <Button icon={<PdfIcon/>} type={"dashed"}>
                      <div>{easaTcoAuthorisationFile ? intl.formatMessage(CommonButtonMessages.replace) : intl.formatMessage(CommonButtonMessages.upload)}</div>
                    </Button>}
                  </CustomUpload>
                </CustomLoadedFileWrapper>

                {/* FAA Authorization - Optional */}
                <CustomLoadedFileWrapper style={{minWidth: 235}}>
                  <CustomLoadedFileWrapperTitle>
                    {intl.formatMessage(ProfileCompanyLicensesMessages.faaAuthorization)}
                  </CustomLoadedFileWrapperTitle>
                  <CustomUpload
                      accept={FileAcceptTypes.DOCUMENT}
                      beforeUpload={(file) => onFileLoad(file, "faaAuthorization")}
                      fileList={faaAuthorizationFile ? [faaAuthorizationFile] : []}
                      maxCount={1}
                      onPreview={onPdfFilePreview}
                      onRemove={isAdmin ? () => onFileRemove("faaAuthorization") : undefined}
                  >
                    {isAdmin && <Button icon={<PdfIcon/>} type={"dashed"}>
                      <div>{faaAuthorizationFile ? intl.formatMessage(CommonButtonMessages.replace) : intl.formatMessage(CommonButtonMessages.upload)}</div>
                    </Button>}
                  </CustomUpload>
                </CustomLoadedFileWrapper>
              </LicensesList>
            </InternationalDocumentsWrapper>
          )}
        </InternationalFlightsToggleWrapper>
      )}

      {isCompanyOnModeration(companyWarningStatus) &&
        <WarningInfo color={companyWarningStatusColor} status={companyWarningStatus}>
          {intl.formatMessage(ProfileCompanyLicensesMessages.warningWaitApprove)}
        </WarningInfo>}

      {isCompanyRejected(companyWarningStatus) &&
        <WarningInfo color={companyWarningStatusColor} status={companyWarningStatus}>
          {intl.formatMessage(ProfileCompanyLicensesMessages.warningRejected)}
        </WarningInfo>}

      {showUnifiedUploadButton && isAdmin && (
        <LoadDocumentsButtonWrapper>
          <CustomButton onClick={onUnifiedUpload} type={"primary"} loading={getButtonLoading()}>
            {getButtonText()}
          </CustomButton>
        </LoadDocumentsButtonWrapper>
      )}
    </LicensesWrapper>
  );
};
